/* global importName: false */
import store from '@/services/store'
import i18n from '@/services/i18n'
import { getDeepProperty } from '@/helpers/deepObject.js'

const GeneralSettings = () => importName('@/scenes/Shows/scenes/Settings/scenes/GeneralSettings', 'generalSettings')
const ManagerSettings = () => importName('@/scenes/Shows/scenes/Settings/scenes/ManagerSettings/ManagerSettings.vue', 'managerSettings')
const DistributionSettings = () => importName('@/scenes/Shows/scenes/Settings/scenes/DistributionSettings/DistributionSettings.vue', 'distributionSettings')
const FeedSettings = () => importName('@/scenes/Shows/scenes/Settings/scenes/DistributionSettings/FeedSettings.vue', 'feedSettings')
const NewFeed = () => importName('@/scenes/Shows/scenes/Settings/scenes/DistributionSettings/NewFeed.vue', 'newFeed')
const MarkerSettings = () => importName('@/scenes/Shows/scenes/Settings/scenes/MarkerSettings', 'markerSettings')
const BlockingPrefs = () => importName('@/scenes/Shows/scenes/Settings/scenes/BlockingPrefs', 'blockingPrefs')
const Marketing = () => importName('@/scenes/Shows/scenes/Settings/scenes/Marketing', 'marketing')
const IntroOutro = () => importName('@/scenes/Shows/scenes/Settings/scenes/IntroOutro', 'introOutro')
const PublishingSchedule = () => importName('@/scenes/Shows/scenes/Settings/scenes/PublishingSchedule', 'publishingSchedule')

const beforeEnter = {

  canEdit: async (to, from, next) => {
    const { activePodcastUserPermissions } = store.getters
    const canEdit = getDeepProperty(activePodcastUserPermissions, 'can_edit')
    if (!canEdit) {
      next({ name: 'show-overview' })
      return
    }
    next()
  }
}

const showSettingsRoutes = [
  {
    path: 'edit',
    name: 'show-settings',
    component: GeneralSettings,
    meta: {
      breadcrumbs: [
        { title: i18n.tc('Settings'), to: 'show-settings' },
        { title: i18n.tc('Show Settings') }
      ]
    },
    beforeEnter: beforeEnter.canEdit
  },
  {
    path: 'edit/:tab',
    name: 'show-settings-tab',
    component: GeneralSettings,
    meta: {
      breadcrumbs: [
        { title: i18n.tc('Settings'), to: 'show-settings' },
        { title: i18n.tc('Show Settings') }
      ]
    },
    beforeEnter: beforeEnter.canEdit
  },
  {
    path: 'managers',
    name: 'show-managers',
    component: ManagerSettings,
    meta: {
      breadcrumbs: [
        { title: i18n.tc('Settings'), to: 'show-settings' },
        { title: i18n.tc('Team Members') }
      ]
    },
    beforeEnter: beforeEnter.canEdit
  },
  {
    path: 'distribution',
    name: 'show-distribution',
    component: DistributionSettings,
    meta: {
      breadcrumbs: [
        { title: i18n.tc('Settings'), to: 'show-settings' },
        { title: i18n.tc('Show Distribution') }
      ]
    },
    beforeEnter: beforeEnter.canEdit
  },
  {
    path: 'marketing',
    name: 'show-marketing',
    component: Marketing,
    meta: {
      breadcrumbs: [
        { title: i18n.tc('Settings'), to: 'show-settings' },
        { title: i18n.tc('Show Marketing') }
      ]
    },
    beforeEnter: async (to, from, next) => {
      const { activePodcastUserPermissions } = store.getters
      const canEdit = getDeepProperty(activePodcastUserPermissions, 'can_edit')
      if (!canEdit) {
        next({ name: 'show-overview' })
        return
      }

      const { activePodcast } = store.getters
      const promos = getDeepProperty(activePodcast, 'features.has_ad_promos') && getDeepProperty(activePodcast, 'promos.href')
      if (promos) {
        store.dispatch('clearPromos').then(response => {
          store.dispatch('getPromos', promos).then(response => {
            store.dispatch('getPromo')
            next()
          })
        })
      }
      next()
    }
  },
  {
    path: 'intro-outro',
    name: 'intro-outro',
    component: IntroOutro,
    meta: {
      breadcrumbs: [
        { title: i18n.tc('Settings'), to: 'show-settings' },
        { title: i18n.tc('intro_outro.title') }
      ]
    },
    beforeEnter: async (to, from, next) => {
      const { activePodcast } = store.getters
      if (getDeepProperty(activePodcast, 'adswizz_enabled') && getDeepProperty(activePodcast, 'intro_outro.href')) {
        next()
        return
      }
      next({ name: 'show-overview' })
    }
  },
  {
    path: 'publishing-schedule',
    name: 'publishing-schedule',
    component: PublishingSchedule,
    meta: {
      breadcrumbs: [
        { title: i18n.tc('Settings'), to: 'show-settings' },
        { title: i18n.tc('publishing_schedule.title') }
      ]
    },
    beforeEnter: async (to, from, next) => {
      const { activePodcast, featureFlags } = store.getters
      if (getDeepProperty(activePodcast, 'cadence') && getDeepProperty(featureFlags, 'episode_scheduling_2023')) {
        next()
        return
      }
      next({ name: 'show-overview' })
    }
  },
  {
    path: 'distribution/feeds',
    redirect: { name: 'show-distribution' }
  },
  {
    path: 'distribution/feeds/new',
    name: 'feed-new',
    component: NewFeed,
    props: true,
    meta: {
      breadcrumbs: [
        { title: i18n.tc('Settings'), to: 'show-settings' },
        { title: i18n.tc('Show Distribution'), to: 'show-distribution'},
        { title: i18n.tc('New Feed') }
      ]
    },
    beforeEnter: async (to, from, next) => {
      const { activePodcast } = store.getters
      const canCreate = getDeepProperty(activePodcast, 'feeds.create')
      // TODO: Might need additional checks here for current number of feeds?
      if (!canCreate) {
        next({ name: 'show-distribution' })
        return
      }
      next()
    }
  },
  {
    path: 'distribution/feeds/:feedId',
    name: 'feed-settings',
    component: FeedSettings,
    props: true,
    meta: {
      breadcrumbs: [
        { title: i18n.tc('Settings'), to: 'show-settings' },
        { title: i18n.tc('Show Distribution'), to: 'show-distribution'},
        { title: i18n.tc('Feed Settings') }
      ]
    },
    beforeEnter: async (to, from, next) => {
      let toFeedId = getDeepProperty(to, 'params.feedId')
      store.dispatch('clearFeed').then(response => {
        store.dispatch('getFeed', toFeedId)
        next()
      })
    }
  },
  {
    path: 'markers',
    name: 'show-markers',
    component: MarkerSettings,
    meta: {
      breadcrumbs: [
        { title: i18n.tc('Settings'), to: 'show-settings' },
        { title: i18n.tc('Marker Settings') }
      ]
    },
    beforeEnter: async (to, from, next) => {
      const { activePodcastUserPermissions } = store.getters
      const canEdit = getDeepProperty(activePodcastUserPermissions, 'can_edit')
      if (!canEdit) {
        next({ name: 'show-overview' })
        return
      }
      next()
    }
  },
  {
    path: 'ad-quality',
    name: 'ad-quality',
    component: BlockingPrefs,
    meta: {
      breadcrumbs: [
        { title: i18n.tc('Settings'), to: 'show-settings' },
        { title: i18n.tc('Ad Quality') }
      ]
    },
    beforeEnter: async (to, from, next) => {
      // Check if the show has the Ad Quality preferences feature
      const { activePodcast, featureFlags } = store.getters
      const adBlockingFlag = getDeepProperty(featureFlags, 'ad_blocking_20230504')
      const hasAdBlocking = getDeepProperty(activePodcast, 'blocked_categories.href') && getDeepProperty(activePodcast, 'blocked_domains.href')

      if (!adBlockingFlag || !hasAdBlocking) {
        next({ name: 'show-overview' })
        return
      }
      next()
    }
  }
]

export default showSettingsRoutes
